import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAsync, parse4DSource } from "../lib";
import { ResultChips, Empty, Skeleton, FlagBadge } from "../ui";

const CAT_LABEL = {
  GOV: "รัฐบาล / สถาบัน",
  FOREIGN: "ต่างประเทศ",
  STOCK: "หุ้น",
  YEEKEE: "ยี่กี",
  "4D": "4D",
};
const CAT_ORDER = ["GOV", "FOREIGN", "STOCK", "YEEKEE", "4D"];
const CAT_ICON = {
  GOV: "solar:buildings-2-bold-duotone",
  FOREIGN: "solar:global-bold-duotone",
  STOCK: "solar:chart-2-bold-duotone",
  YEEKEE: "fluent-emoji-flat:alarm-clock",
  "4D": "solar:hashtag-square-bold-duotone",
};

function dateKey(iso) {
  if (!iso) return "ไม่ทราบวันที่";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "ไม่ทราบวันที่";
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "วันนี้";
  if (same(d, yest)) return "เมื่อวาน";
  return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });
}

const fmtIdx = (v) =>
  Number(v).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// หวยหุ้น 4D: บอกลูกค้าว่าออกผลจากราคาอะไร — โชว์เฉพาะตลาดเปิด (ราคาจริง ณ นาทีออกผล); วันตลาดปิดไม่แสดงที่มาของผล
function FourDSource({ raw }) {
  const s = parse4DSource(raw);
  if (!s || s.m !== "LIVE") return null;
  const pct = Number(s.pct);
  const pctText = Number.isFinite(pct) ? `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%` : "";
  return <div className="lx-4d-src live">{`ตลาดเปิด · ${fmtIdx(s.p)} (${pctText})`}</div>;
}

function TypeLabel({ r, typeById, size = 40 }) {
  const t = typeById[r.lotto_type_id];
  return (
    <div className="vx-res-id">
      <FlagBadge code={t?.code || r.type_code} category={t?.category} size={size} />
      <div className="vx-res-name">
        <b>{r.type_name}</b>
        <span>{r.round_label}</span>
        {r.raw_value && <FourDSource raw={r.raw_value} />}
      </div>
    </div>
  );
}

const PAGE = 100;

// ทั้งหมด: ยี่กีเหลือรอบล่าสุดของแต่ละชนิด, แท็บยี่กี: เฉพาะผลวันนี้ (วันเล่นปัจจุบัน)
function resultQuery(cat, typeId) {
  const yeekee = cat === "YEEKEE" ? "today" : cat === "" ? "latest" : undefined;
  if (typeId) return { lottoTypeId: typeId, yeekee };
  if (cat) return { category: cat, yeekee };
  return { yeekee };
}

export default function ResultsPage() {
  const { data: types } = useAsync(() => lottoApi.types(), []);
  const [cat, setCat] = useState("");
  const [typeId, setTypeId] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const reqId = useRef(0);

  // ดึงใหม่ทุกครั้งที่เปลี่ยนหมวด/ชนิด — ให้ server กรอง จะได้ไม่โดนยี่กีหลายร้อยรอบดันผลหมวดอื่นหลุด limit
  // reqId กันผลของคำขอเก่าที่ตอบกลับช้ามาทับแท็บที่เลือกอยู่
  const fetchPage = useCallback(
    async (offset) => {
      const id = ++reqId.current;
      if (offset) setLoadingMore(true);
      else setLoading(true);
      try {
        const rows = (await lottoApi.results({ ...resultQuery(cat, typeId), limit: PAGE, offset })) || [];
        if (id !== reqId.current) return;
        setList((prev) => (offset ? [...prev, ...rows] : rows));
        setHasMore(rows.length === PAGE);
      } catch {
        if (id !== reqId.current) return;
        if (!offset) setList([]);
        setHasMore(false);
      } finally {
        if (id === reqId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [cat, typeId],
  );

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  const typeById = useMemo(() => {
    const m = {};
    (types || []).forEach((t) => (m[t.id] = t));
    return m;
  }, [types]);

  const catTypes = useMemo(
    () => (types || []).filter((t) => !cat || t.category === cat),
    [types, cat],
  );

  // ผลล่าสุดของแต่ละชนิด (1 ใบ/ชนิด) สำหรับดูภาพรวมเร็ว ๆ — API เรียง result_at ล่าสุดก่อนอยู่แล้ว
  const latestByType = useMemo(() => {
    const seen = new Map();
    list.forEach((r) => {
      if (!seen.has(r.lotto_type_id)) seen.set(r.lotto_type_id, r);
    });
    return [...seen.values()];
  }, [list]);

  // ประวัติทั้งหมด จัดกลุ่มตามวันที่ — ใช้ Map รวมแถววันเดียวกันไว้กลุ่มเดียว
  // (API เรียงตามเวลาออกผล ส่วนกลุ่มใช้วันงวด งวดที่ยืนยันผลช้าจะได้ไม่ทำให้หัววันที่ซ้ำ)
  const groups = useMemo(() => {
    const byKey = new Map();
    list.forEach((r) => {
      const key = dateKey(r.draw_date || r.result_at);
      if (!byKey.has(key)) byKey.set(key, { key, rows: [] });
      byKey.get(key).rows.push(r);
    });
    return [...byKey.values()];
  }, [list]);

  const selectCat = (c) => {
    setCat(c);
    setTypeId("");
  };

  return (
    <div className="vx-results" style={{ marginTop: 18 }}>
      <header className="lx-card vx-pagehead vx-res-head">
        <div className="vx-res-head-copy">
          <span className="vx-kicker">
            <Icon icon="fluent-emoji-flat:trophy" width={16} height={16} />
            ตรวจผลรางวัล
          </span>
          <h1 className="lx-h1">ผลรางวัลย้อนหลัง</h1>
          <p>รวมผลหวยทุกชนิด อัปเดตทันทีที่ออกผล — กรองตามหมวดหรือชนิดหวยด้านล่าง</p>
        </div>
        <div className="vx-res-head-art" aria-hidden="true">
          <Icon icon="fluent-emoji-flat:trophy" className="a1" />
          <Icon icon="fluent-emoji-flat:sparkles" className="a2" />
          <Icon icon="fluent-emoji-flat:star" className="a3" />
        </div>
      </header>

      <div className="vx-fchips" role="tablist" aria-label="หมวดหวย">
        <button role="tab" aria-selected={cat === ""} className={cat === "" ? "on" : ""} onClick={() => selectCat("")}>
          <Icon icon="solar:widget-5-bold-duotone" width={17} height={17} />
          ทั้งหมด
        </button>
        {CAT_ORDER.filter((c) => (types || []).some((t) => t.category === c)).map((c) => (
          <button key={c} role="tab" aria-selected={cat === c} className={cat === c ? "on" : ""} onClick={() => selectCat(c)}>
            <Icon icon={CAT_ICON[c]} width={17} height={17} />
            {CAT_LABEL[c] || c}
          </button>
        ))}
      </div>

      {!!cat && catTypes.length > 1 && (
        <div className="lx-type-chips vx-res-types">
          <button className={`all ${typeId === "" ? "on" : ""}`} onClick={() => setTypeId("")}>
            <span>ทุกชนิดในหมวดนี้</span>
          </button>
          {catTypes.map((t) => (
            <button
              key={t.id}
              className={typeId === String(t.id) ? "on" : ""}
              onClick={() => setTypeId(String(t.id))}
              title={t.name_th}
            >
              <FlagBadge code={t.code} category={t.category} size={20} />
              <span>{t.name_th}</span>
            </button>
          ))}
        </div>
      )}

      {(cat === "" || cat === "YEEKEE") && (
        <p className="vx-res-hint">
          <Icon icon="solar:info-circle-bold-duotone" width={15} height={15} />
          {cat === "YEEKEE"
            ? "แสดงผลยี่กีเฉพาะของวันนี้"
            : "ยี่กีแสดงเฉพาะรอบล่าสุด — ดูทุกรอบของวันนี้ได้ที่แท็บยี่กี"}
        </p>
      )}

      {loading && (
        <div className="vx-res-grid" style={{ marginTop: 16 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} h={150} />
          ))}
        </div>
      )}

      {!loading && !list.length && (
        <div className="lx-card" style={{ marginTop: 16 }}>
          <Empty icon="solar:hourglass-line-bold-duotone">
            {cat === "YEEKEE" ? "ยังไม่มีผลยี่กีของวันนี้" : "ยังไม่มีผลรางวัลในหมวดนี้"}
          </Empty>
        </div>
      )}

      {!loading && !typeId && latestByType.length > 1 && (
        <section className="vx-block">
          <div className="vx-block-head">
            <div className="vx-block-title">
              <span className="vx-block-ic cat-b">
                <Icon icon="solar:medal-ribbons-star-bold-duotone" width={18} height={18} />
              </span>
              <h2 className="lx-h2">ผลล่าสุดของแต่ละชนิด</h2>
              <span className="lx-count-badge">{latestByType.length}</span>
            </div>
          </div>
          <div className="vx-res-grid">
            {latestByType.map((r, i) => (
              <article className={`vx-res-card ${i % 2 ? "cat-b" : "cat-a"}`} key={`latest-${r.lotto_type_id}`}>
                <div className="vx-res-card-top">
                  <TypeLabel r={r} typeById={typeById} size={44} />
                </div>
                <div className="vx-res-card-body">
                  <ResultChips result={r} size="sm" />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {!loading && !!groups.length && (
        <section className="vx-block">
          <div className="vx-block-head">
            <div className="vx-block-title">
              <span className="vx-block-ic">
                <Icon icon="solar:history-bold-duotone" width={18} height={18} />
              </span>
              <h2 className="lx-h2">ประวัติผลรางวัล</h2>
            </div>
          </div>
          <div className="vx-tl">
            {groups.map((g) => (
              <div key={g.key} className="vx-tl-group">
                <div className="vx-tl-day">
                  <span>{g.key}</span>
                  <i>{g.rows.length} ผล</i>
                </div>
                <div className="vx-tl-list">
                  {g.rows.map((r) => (
                    <div className="vx-res-row" key={r.round_id}>
                      <TypeLabel r={r} typeById={typeById} size={40} />
                      <div className="vx-res-nums">
                        <ResultChips result={r} size="sm" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              className="lx-btn ghost block"
              style={{ marginTop: 8 }}
              disabled={loadingMore}
              onClick={() => fetchPage(list.length)}
            >
              {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่ม"}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
